import { Deferred } from "@inertiajs/react";
import { format } from "date-fns";
import {
	Calendar,
	ClipboardListIcon,
	Copy,
	Fingerprint,
	Loader2,
	Tag,
} from "lucide-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { orderStatusStyle } from "@/lib/orders/status-styles";
import { getRegistrationSourceLabel } from "@/lib/patients";
import { cn, copyToClipboard } from "@/lib/utils";
import type { UserDetailOrders, UserForAdmin } from "@/types/admin-portal/user";

export interface UserDetailContentProps {
	user: UserForAdmin | null;
	orders: UserDetailOrders | null;
	isLoading?: boolean;
	open: boolean;
	onOpenChange?: (open: boolean) => void;
	onPageChange: (page: number, limit?: number) => void;
}

const UserDetailContent = memo(function Component({
	user,
	orders,
	isLoading,
	open,
	onOpenChange,
	onPageChange,
}: UserDetailContentProps) {
	const { t: td } = useTranslation("translation", { keyPrefix: "components" });
	const { t } = useTranslation("users", { keyPrefix: "details" });
	const { t: tl } = useTranslation("users", { keyPrefix: "list" });

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<DrawerContent className="flex flex-col rounded-t-[10px] mt-24 max-h-[100%] fixed bottom-0 left-0 right-0 outline-none">
				<div className="w-full max-w-sm mx-auto md:max-w-5xl">
					<DrawerHeader>
						<DrawerTitle>{t("title")}</DrawerTitle>
						<DrawerDescription>{t("description")}</DrawerDescription>
					</DrawerHeader>

					<div className="p-4">
						<Tabs defaultValue="profile" className="w-full">
							<TabsList className="w-full justify-start">
								<TabsTrigger value="profile" className="w-full md:w-auto">
									{t("tabs.profile")}
								</TabsTrigger>
								<TabsTrigger value="orders" className="w-full md:w-auto">
									{t("tabs.orders")}
								</TabsTrigger>
							</TabsList>

							<TabsContent
								value="profile"
								className="flex flex-col gap-4 max-h-[500px] overflow-y-auto mt-0"
							>
								{isLoading ? (
									<div className="flex flex-col items-center justify-center py-20 gap-4">
										<Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
										<p className="text-sm text-muted-foreground animate-pulse">
											{t("loading_details", "Loading user details...")}
										</p>
									</div>
								) : user ? (
									<div className="flex flex-col gap-4 my-2">
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<Card className="shadow-inner">
												<CardHeader>
													<p className="text-sm font-medium uppercase opacity-75">
														{t("basic_info")}
													</p>
												</CardHeader>
												<CardContent>
													<div className="flow-root">
														<dl className="-my-2 text-sm grid grid-cols-1 gap-2">
															{/* we disabled this for now until this data is needed  */}
															{/* <InfoRow
																label={t("first_name")}
																value={user.firstName || "N/A"}
															/>
															<InfoRow
																label={t("last_name")}
																value={user.lastName || "N/A"}
															/> */}
															<InfoRow label={t("email")}>
																<div className="flex items-center gap-1 group/copy">
																	<span className="break-all text-pretty">
																		{user.email}
																	</span>
																	<Button
																		variant="ghost"
																		size="icon"
																		className="h-4 w-4 opacity-0 group-hover/copy:opacity-100 transition-opacity"
																		onClick={() => {
																			copyToClipboard(user.email);
																			toast.success(
																				t("messages.copied_to_clipboard"),
																			);
																		}}
																	>
																		<Copy className="h-3 w-3" />
																	</Button>
																</div>
															</InfoRow>
															<InfoRow label={t("phone")}>
																<div className="flex items-center gap-1 group/copy">
																	<span className="font-mono tracking-tighter">
																		{user.phoneNumber || "N/A"}
																	</span>
																	{user.phoneNumber && (
																		<Button
																			variant="ghost"
																			size="icon"
																			className="h-4 w-4 opacity-0 group-hover/copy:opacity-100 transition-opacity"
																			onClick={() => {
																				copyToClipboard(
																					user.phoneNumber as string,
																				);
																				toast.success(
																					t("messages.copied_to_clipboard"),
																				);
																			}}
																		>
																			<Copy className="h-3 w-3" />
																		</Button>
																	)}
																</div>
															</InfoRow>
															<InfoRow label={t("roles")}>
																<div className="flex flex-wrap gap-1">
																	{user.roles.map((role) => (
																		<Badge
																			key={role}
																			variant="accent"
																			className="px-1.5 py-1 text-[10px] font-bold uppercase rounded-full"
																		>
																			{role}
																		</Badge>
																	))}
																</div>
															</InfoRow>
															<InfoRow label={t("verified")}>
																<Badge
																	variant="accent"
																	className={cn(
																		"px-1.5 py-1 rounded-full",
																		user.isVerified
																			? "bg-green-100"
																			: "bg-red-100",
																	)}
																>
																	<span
																		className={cn(
																			"text-[10px] uppercase font-bold",
																			user.isVerified
																				? "text-emerald-500"
																				: "text-red-500",
																		)}
																	>
																		{user.isVerified
																			? tl("verified.verified")
																			: tl("verified.unverified")}
																	</span>
																</Badge>
															</InfoRow>
															<InfoRow label={t("registration_source")}>
																<span className="uppercase italic">
																	{getRegistrationSourceLabel(
																		user.registrationSource,
																	)}
																</span>
															</InfoRow>
															<InfoRow label={tl("orders_count")}>
																<Badge
																	variant="accent"
																	className="px-1 rounded-full"
																>
																	<p className="flex items-center gap-1 font-medium">
																		<ClipboardListIcon className="size-3" />
																		<span>{user.orderCount || 0} Orders</span>
																	</p>
																</Badge>
															</InfoRow>
														</dl>
													</div>
												</CardContent>
											</Card>

											<Card className="shadow-inner">
												<CardHeader>
													<p className="text-sm font-medium uppercase opacity-75">
														{t("account_activity")}
													</p>
												</CardHeader>
												<CardContent>
													<div className="flow-root">
														<dl className="-my-2 text-sm grid grid-cols-1 gap-2">
															<InfoRow label={tl("status.label")}>
																<Badge
																	variant="accent"
																	className={cn(
																		"px-1.5 py-1 rounded-full",
																		user.isSuspended
																			? "bg-red-100"
																			: "bg-green-100",
																	)}
																>
																	<span
																		className={cn(
																			"text-[10px] uppercase font-bold",
																			user.isSuspended
																				? "text-red-500"
																				: "text-emerald-500",
																		)}
																	>
																		{user.isSuspended
																			? tl("status.suspended")
																			: tl("status.active")}
																	</span>
																</Badge>
															</InfoRow>
															<InfoRow label={t("sign_in_count")}>
																<Badge
																	variant="accent"
																	className="px-1 rounded-full"
																>
																	<p className="flex items-center gap-1 font-medium">
																		<Fingerprint className="size-3" />
																		<span>
																			{user.signInCount || 0} Sign-ins
																		</span>
																	</p>
																</Badge>
															</InfoRow>
															<InfoRow label={t("sign_in_ip")}>
																<span className="font-mono tracking-tighter italic">
																	{user.lastSignInIp || "N/A"}
																</span>
															</InfoRow>
															<InfoRow label={t("last_sign_in")}>
																<span className="tracking-tight text-pretty italic">
																	{user.lastSignInAt
																		? format(
																				new Date(user.lastSignInAt),
																				"PPpp",
																			)
																		: "Never"}
																</span>
															</InfoRow>
															<InfoRow label={t("registered_at")}>
																<span className="tracking-tight text-pretty italic">
																	{format(new Date(user.createdAt), "PPpp")}
																</span>
															</InfoRow>
															{user.isSuspended && user.suspendAt && (
																<InfoRow
																	label={t("suspension_period")}
																	value={`${format(
																		new Date(user.suspendAt),
																		"PP",
																	)} - ${
																		user.suspendEnd
																			? format(new Date(user.suspendEnd), "PP")
																			: "Permanent"
																	}`}
																/>
															)}
														</dl>
													</div>
												</CardContent>
											</Card>
										</div>
									</div>
								) : null}
							</TabsContent>

							<TabsContent
								value="orders"
								className="flex flex-col gap-4 max-h-[73dvh] overflow-y-auto mt-0"
							>
								<Deferred data="userOrders" fallback={<LoadingOrders />}>
									<div className="p-2 md:p-4 py-4 space-y-6">
										{!orders?.data?.length ? (
											<p className="text-sm font-light text-center text-muted-foreground">
												{t("orders_empty")}
											</p>
										) : (
											<>
												<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
													{orders.data.map((order) => (
														<Card
															key={order.id}
															className="shadow-none border border-border"
														>
															<CardContent className="p-2">
																<div className="flex flex-col gap-3">
																	<div className="flex items-center justify-between">
																		<div className="flex items-center gap-2">
																			<div className="bg-primary/10 p-2 rounded-lg text-primary">
																				<Tag className="w-4 h-4" />
																			</div>
																			<div className="flex flex-col">
																				<p className="text-sm font-bold uppercase tracking-tight">
																					{order.registrationNumber}
																				</p>
																				<p className="text-[10px] text-muted-foreground font-mono">
																					{order.invoiceNumber || "NO INVOICE"}
																				</p>
																			</div>
																		</div>
																		<Badge
																			variant="accent"
																			className={cn(
																				"px-2 py-0.5 rounded-full text-[10px] font-bold uppercase",
																				orderStatusStyle(order.status),
																			)}
																		>
																			{order.status}
																		</Badge>
																	</div>

																	<div className="grid grid-cols-2 gap-4 border-t pt-3 border-dashed">
																		<div className="flex flex-col gap-1">
																			<span className="text-[10px] uppercase font-medium text-muted-foreground/75 tracking-tight">
																				Package & Service
																			</span>
																			<div className="flex items-center gap-1.5">
																				<span className="text-xs font-semibold truncate tracking-tight uppercase">
																					{order.serviceName}
																				</span>
																			</div>
																			<span className="text-[10px] text-muted-foreground tracking-tight uppercase">
																				{order.packageName} &mdash;{" "}
																				<span className="italic normal-case">
																					{order.numberOfVisit} Total Visit's
																				</span>
																			</span>
																		</div>
																		<div className="flex flex-col gap-1 items-end text-right">
																			<span className="text-[10px] uppercase font-medium text-muted-foreground/75 tracking-tight">
																				Total Amount
																			</span>
																			<div className="flex items-center gap-1.5">
																				<span className="text-xs font-bold text-primary">
																					{new Intl.NumberFormat("id-ID", {
																						style: "currency",
																						currency: "IDR",
																						maximumFractionDigits: 0,
																					}).format(order.totalAmount || 0)}
																				</span>
																			</div>
																			<Badge
																				variant="outline"
																				className={cn(
																					"mt-1 px-1.5 rounded-md text-[9px] font-medium border-0 rounded-full",
																					order.paymentStatus === "PAID"
																						? "bg-emerald-50 text-emerald-600"
																						: "bg-amber-50 text-amber-600",
																				)}
																			>
																				{order.paymentStatus}
																			</Badge>
																		</div>
																	</div>

																	<div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1 bg-muted p-2 border rounded-md">
																		<div className="flex items-center gap-1.5">
																			<Calendar className="w-3 h-3 opacity-75" />
																			<p>
																				<span>Ordered: </span>
																				<span className="font-medium">
																					{format(
																						new Date(order.createdAt),
																						"PPP",
																					)}
																				</span>
																			</p>
																		</div>
																		<p>
																			<span>For: </span>
																			<span className="font-medium">
																				{order.patientName}
																			</span>
																		</p>
																	</div>
																</div>
															</CardContent>
														</Card>
													))}
												</div>

												{orders.metadata && (
													<ApptPagination
														className="mt-2"
														metadata={orders.metadata}
														actions={{
															goToPrevpage: () =>
																onPageChange(
																	orders.metadata.page - 1,
																	orders.metadata.limit,
																),
															goToNextPage: () =>
																onPageChange(
																	orders.metadata.page + 1,
																	orders.metadata.limit,
																),
															goToPage: (value) =>
																onPageChange(
																	Number(value),
																	orders.metadata.limit,
																),
															goToFirstPage: () =>
																onPageChange(1, orders.metadata.limit),
															goToLastPage: () =>
																onPageChange(
																	orders.metadata.pages,
																	orders.metadata.limit,
																),
															onChangeLimit: (value) =>
																onPageChange(1, Number(value)),
														}}
													/>
												)}
											</>
										)}
									</div>
								</Deferred>
							</TabsContent>
						</Tabs>
					</div>

					<DrawerFooter>
						<DrawerClose asChild>
							<Button
								type="button"
								variant="outline"
								size="lg"
								className="shadow-none bg-card"
							>
								{td("modal.close")}
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
});

function LoadingOrders() {
	return (
		<div className="flex flex-col gap-4">
			{[1, 2, 3].map((i) => (
				<Card key={i} className="shadow-sm border-muted animate-pulse">
					<CardContent className="p-4">
						<div className="flex flex-col gap-3">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<div className="bg-muted p-2 rounded-lg size-8" />
									<div className="flex flex-col gap-2">
										<div className="h-4 w-32 bg-muted rounded" />
										<div className="h-3 w-20 bg-muted rounded" />
									</div>
								</div>
								<div className="h-5 w-16 bg-muted rounded-full" />
							</div>
							<div className="grid grid-cols-2 gap-4 border-t pt-3 border-dashed mt-1">
								<div className="h-10 bg-muted rounded" />
								<div className="h-10 bg-muted rounded" />
							</div>
						</div>
					</CardContent>
				</Card>
			))}
		</div>
	);
}

function InfoRow({
	label,
	value,
	children,
}: {
	label: string;
	value?: string;
	children?: React.ReactNode;
}) {
	return (
		<div className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-3 sm:gap-4">
			<dt className="text-muted-foreground/75">{label}</dt>
			<dd className="sm:col-span-2 font-medium">
				{children || <span>{value}</span>}
			</dd>
		</div>
	);
}

export default UserDetailContent;
